import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Buffer } from 'buffer';
import './index.css';

const Privacy = () => {
  const location = useLocation();
  const unsubscribe_url = process.env.REACT_APP_UNSUBSCRIBE_URL || "";
  const EXPECTED_API_KEY = process.env.REACT_APP_UNSUBSCRIBE_API_KEY || process.env.REACT_APP_CONTACT_API_KEY || "091cb55d-d130-4536-ab37-46a3f91e85dc";

  const [email, setEmail] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string>('');

  useEffect(() => {
    // Extract email from URL parameters
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [location]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleUnsubscribe = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!email) {
      setSubmitStatus('Email address is required');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('Processing...');

    const x_api_key = Buffer.from(EXPECTED_API_KEY, 'utf-8').toString('base64');

    if (!unsubscribe_url) {
      setSubmitStatus('Unsubscribe endpoint not configured. Please contact support.');
      setIsSubmitting(false);
      return;
    }

    const formObject = {
      email: email,
      source: 'email_link',
      notes: 'Unsubscribe request from privacy page'
    };

    const formJson = JSON.stringify(formObject);

    try {
      const response = await fetch(unsubscribe_url, {
        method: 'POST',
        body: formJson,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': x_api_key
        }
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          // Try to get error details from response body
          const errorText = await response.text();
          if (errorText) {
            // If it's JSON, try to parse it
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
              // If not JSON, use the text as is (truncated if too long)
              errorMessage = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText;
            }
          }
        } catch (textError) {
          console.warn('Could not read error response body:', textError);
        }
        
        setSubmitStatus(`Error: ${errorMessage}`);
        setIsSubmitted(false);
        setIsSubmitting(false);
        return;
      }

      // Parse JSON response - wrap in try/catch in case response isn't JSON
      try {
        const data = await response.json();
        console.log('Unsubscribe successful:', data);
        setIsSubmitted(true);
        setSubmitStatus('');
      } catch (jsonError) {
        // If JSON parsing fails, but response was ok, assume success
        console.warn('Could not parse JSON response, but request succeeded:', jsonError);
        setIsSubmitted(true);
        setSubmitStatus('');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Check if this is a network error vs other error
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        // For network errors, still try to show success since the request might have gone through
        // Many times the request succeeds but the response can't be read due to CORS or network issues
        console.warn('Network error occurred, but request may have succeeded. Check Lambda logs.');
        setSubmitStatus('Request submitted. If you don\'t receive a confirmation email, please contact support.');
        setIsSubmitted(true); // Assume success since Lambda is processing
      } else if (errorMessage.includes('CORS')) {
        setSubmitStatus('CORS error: Cross-origin request blocked. Please contact support.');
        setIsSubmitted(false);
      } else {
        setSubmitStatus(`An error occurred: ${errorMessage}. Please try again or contact us directly.`);
        setIsSubmitted(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="privacy-container">
      {isSubmitted ? (
        <div className="success-message">
          <br />
          <p>You have been successfully unsubscribed.</p>
          <p>We will process your request and remove your email from our mailing list.</p>
        </div>
      ) : (
        <div className="privacy-form">
          <h1>Privacy & Unsubscribe</h1>
          <form name="privacy" id="privacy" onSubmit={handleUnsubscribe}>
            <div className="form-row">
              <div className="form-column">
                <label htmlFor="email">
                  Email Address
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={email} 
                  onChange={handleEmailChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <button 
                type="submit" 
                className={email && !isSubmitting ? 'button_enabled' : 'button_disabled'}
                disabled={!email || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Unsubscribe'}
              </button>
            </div>


            {submitStatus && (
              <div className="form-row">
                <span className="submit-status">{submitStatus}</span>
              </div>
            )}
          </form>
          <div className="privacy-notice">
            <p>
              Hogcloud is committed to protecting your privacy and abides by all applicable privacy 
              and electronic solicitation laws, including but not limited to the CAN-SPAM Act, 
              GDPR, and other relevant regulations.
            </p>
            <p>
            If you no longer wish to receive email, text, or telephone solicitations from us, you may opt out at any time.
            </p>
            <p>
              We do not sell, rent, trade, or share mobile phone numbers with third parties for their marketing or promotional purposes. Mobile numbers are used only to provide Hogcloud communications, customer service, order updates, program messages, or other communications you request or authorize.
            </p>
            <p>
              Message frequency may vary depending on your orders, requests, account activity, program participation, or communication preferences. Message and data rates may apply.
            </p>
            <ul>
              <li>To stop emails, click the “Unsubscribe” link in any email we sent you — or send a reply to that email indicating you want to be removed.</li>
              <li>To stop text messages, reply “STOP” or follow the unsubscribe instructions included in the text.</li>
              <li>To stop telephone solicitations, email us or call us to request removal.</li>
            </ul>
            <p>Once you opt out, we will promptly honor your request. You will no longer receive promotional emails or calls from us unless you give your prior express consent again (or re-establish a business relationship).</p>
          </div>
          <div>
              <p>We respect your privacy and comply with applicable law — including:</p>
              <ul className="privacy-laws">
                <li>Arkansas Unsolicited Commercial and Sexually Explicit Electronic Mail Prevention Act <a href="https://law.justia.com/codes/arkansas/title-4/subtitle-7/chapter-88/subchapter-6/section-4-88-603/" target="_blank" rel="noopener noreferrer">(A.C.A. §§ 4-88-601-607)</a> — which requires senders to provide a no-cost, simple way to unsubscribe.</li>
                <li>Arkansas Consumer Telephone Privacy Act and related telemarketing statutes <a href="https://law.justia.com/codes/arkansas/title-4/subtitle-7/chapter-99/subchapter-4/section-4-99-401/" target="_blank" rel="noopener noreferrer">(A.C.A. Title 4, Chapter 99)</a> — which prohibit sending solicitations to numbers on the state/national do-not-call list or after a consumer requests no further contact</li>
                <li>The Federal Communications Commission’s Telephone Consumer Protection Act (TCPA) — which prohibits sending unsolicited text messages to mobile phones without prior express consent</li>
                <li><a href="https://www.fcc.gov/tags/telephone-consumer-protection-act-tcpa/" target="_blank" rel="noopener noreferrer">The Federal Communications Commission’s Telephone Consumer Protection Act (TCPA)</a> — which prohibits sending unsolicited text messages to mobile phones without prior express consent</li>
                <li><a href="https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business/" target="_blank" rel="noopener noreferrer">The CAN-SPAM Act</a> — which requires senders to provide a no-cost, simple way to unsubscribe.</li>
              </ul>
          </div>


        </div>
      )}
    </div>
  );
};

export default Privacy;
